import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Timer, ChevronDown, ExternalLink, Square, Coffee, Users, Phone, Moon } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useActiveSession, useLiveElapsed, fmtElapsed } from "@/context/ActiveSessionContext";

// Visual kit for each activity kind
const KIND_META = {
  task:    { icon: Timer,  color: "emerald", label: "Working on" },
  break:   { icon: Coffee, color: "amber",   label: "On break" },
  meeting: { icon: Users,  color: "sky",     label: "In meeting" },
  call:    { icon: Phone,  color: "violet",  label: "On call" },
  idle:    { icon: Moon,   color: "slate",   label: "Unassigned" },
};

const COLORS = {
  emerald: { badge: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900", dot: "bg-emerald-600", icon: "text-emerald-700", chip: "text-emerald-700" },
  amber:   { badge: "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-900",         dot: "bg-amber-600",   icon: "text-amber-700",   chip: "text-amber-700" },
  sky:     { badge: "border-sky-200 bg-sky-50 hover:bg-sky-100 text-sky-900",                 dot: "bg-sky-600",     icon: "text-sky-700",     chip: "text-sky-700" },
  violet:  { badge: "border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-900",     dot: "bg-violet-600",  icon: "text-violet-700",  chip: "text-violet-700" },
  slate:   { badge: "border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800",         dot: "bg-slate-500",   icon: "text-slate-600",   chip: "text-slate-600" },
};

export default function ActiveTaskBadge() {
  const { session, notifyStopped, openStopModal, openEndSegmentModal } = useActiveSession();
  const location = useLocation();
  const nav = useNavigate();
  const elapsed = useLiveElapsed(session);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  if (!session) return null;
  const kind = session.kind || (session.task_id ? "task" : "idle");
  // Hide on the running task's own detail page (§21 no duplicate indicator)
  if (kind === "task" && location.pathname === `/tasks/${session.task_id}`) return null;

  const meta = KIND_META[kind] || KIND_META.idle;
  const Icon = meta.icon;
  const clr = COLORS[meta.color];
  const primaryLabel = kind === "task"
    ? (session.parent_task_name ? `${session.parent_task_name} › ${session.task_name}` : session.task_name)
    : (session.reason || meta.label);

  const goToTask = () => { setOpen(false); if (session.task_id) nav(`/tasks/${session.task_id}`); };

  const stopTaskFromBadge = () => {
    setOpen(false);
    openStopModal();
  };

  const endActivitySegment = () => {
    setOpen(false);
    // Break/Meeting/Call require reason before stopping → open the End Segment modal
    if (["break", "meeting", "call"].includes(kind)) {
      openEndSegmentModal();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="active-task-badge"
          data-kind={kind}
          className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors text-xs ${clr.badge}`}
        >
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full rounded-full ${clr.dot} opacity-60 animate-ping`} />
            <span className={`relative inline-flex rounded-full h-2 w-2 ${clr.dot}`} />
          </span>
          <Icon className={`w-3.5 h-3.5 ${clr.icon}`} strokeWidth={1.75} />
          <span data-testid="active-task-elapsed" className="mono font-medium tabular-nums">{fmtElapsed(elapsed)}</span>
          <span data-testid="active-task-name" className="max-w-[220px] truncate">{primaryLabel}</span>
          <ChevronDown className={`w-3.5 h-3.5 ${clr.icon}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-80 p-4" data-testid="active-task-popover">
        <div className={`flex items-center gap-2 text-[11px] uppercase tracking-wider ${clr.chip}`}>
          <Icon className="w-3.5 h-3.5" /> {meta.label}
        </div>
        <div className="text-sm font-semibold mt-1 leading-snug break-words" data-testid="popover-task-name">{primaryLabel}</div>
        <div className="mono text-3xl font-semibold tabular-nums mt-3" data-testid="popover-elapsed">{fmtElapsed(elapsed)}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">Started {new Date(session.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {kind === "task" ? (
            <>
              <Button data-testid="popover-go-to-task" variant="outline" size="sm" onClick={goToTask} className="gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </Button>
              <Button data-testid="popover-stop-btn" size="sm" disabled={busy} onClick={stopTaskFromBadge} className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
                <Square className="w-3.5 h-3.5 fill-current" /> Stop
              </Button>
            </>
          ) : ["break", "meeting", "call"].includes(kind) ? (
            <Button data-testid="popover-end-segment" size="sm" disabled={busy} onClick={endActivitySegment} className="col-span-2 bg-slate-800 hover:bg-slate-900 text-white gap-1.5">
              End {meta.label.toLowerCase()}
            </Button>
          ) : (
            <div className="col-span-2 text-[11px] text-slate-500 text-center pt-1">Start a task from the Tasks page to switch out of Idle.</div>
          )}
        </div>
        {kind !== "task" && kind !== "idle" && (
          <div className="text-[11px] text-slate-500 mt-2 text-center">You'll be asked for a reason on end.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
